import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ClockIcon,
  CalendarIcon,
  TrashIcon,
  TimerIcon,
} from '../common/IconsComponent';

/* ─── UI Status Map ───────────────────────────────────────────────────────── */

const STATUS_MAP = {
  NO_RESPONSE: {
    className: 'bg-amber-50 text-amber-800 border-amber-200',
    label: 'ממתין לאישור',
    accentColor: '#BA7517',
    thumbBg: 'bg-amber-50',
    thumbIconColor: '#BA7517',
  },
  APPROVED: {
    className: 'bg-green-50 text-green-800 border-green-200',
    label: 'מאושר',
    accentColor: '#1D9E75',
    thumbBg: 'bg-blue-50',
    thumbIconColor: '#185FA5',
  },
  NOT_APPROVED: {
    className: 'bg-red-50 text-red-800 border-red-200',
    label: 'נדחה',
    accentColor: '#E24B4A',
    thumbBg: 'bg-red-50',
    thumbIconColor: '#A32D2D',
  },
  PAID: {
    className: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    label: 'שולם',
    accentColor: '#534AB7',
    thumbBg: 'bg-indigo-50',
    thumbIconColor: '#534AB7',
  },
  PAYMENT_EXPIRED: {
    className: 'bg-red-50 text-red-800 border-red-200',
    label: 'פג תוקף התשלום',
    accentColor: '#E24B4A',
    thumbBg: 'bg-red-50',
    thumbIconColor: '#A32D2D',
  },
  REQUEST_EXPIRED: {
    className: 'bg-orange-50 text-orange-800 border-orange-200',
    label: 'לא טופל בזמן',
    accentColor: '#993C1D',
    thumbBg: 'bg-orange-50',
    thumbIconColor: '#993C1D',
  },
};

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

const normalizeStatus = (status) => {
  const s = String(status || '').trim().toUpperCase();
  if (s === 'PENDING') return 'NO_RESPONSE';
  if (s === 'REJECTED') return 'NOT_APPROVED';
  return s || 'NO_RESPONSE';
};

const toLocalDateOnly = (ymd) => {
  if (!ymd) return null;
  if (ymd instanceof Date) return Number.isNaN(ymd.getTime()) ? null : ymd;
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [Y, M, D] = ymd.split('-').map(Number);
    return new Date(Y, M - 1, D, 0, 0, 0, 0);
  }
  const d = new Date(ymd);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const safeTime = String(timeStr).slice(0, 5);
  const [Y, M, D] = String(dateStr).split('-').map(Number);
  const [h, m] = safeTime.split(':').map(Number);
  const d = new Date(Y, (M || 1) - 1, D || 1, h || 0, m || 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatHeDate = (v) => {
  const d = toLocalDateOnly(v);
  return d ? d.toLocaleDateString('he-IL') : '';
};

const formatHeDayMonth = (v) => {
  const d = toLocalDateOnly(v);
  if (!d) return { day: '', month: '' };
  const day = d.getDate();
  const month = d.toLocaleDateString('he-IL', { month: 'short' });
  return { day, month };
};

const formatCountdown = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0) return '00:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const formatDeadlineTime = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
};

const isPaidRequest = (request) =>
  request?.paid === true ||
  request?.isPaid === true ||
  request?.purchased === true ||
  String(request?.paymentStatus || '').toUpperCase() === 'PAID' ||
  String(request?.payment_status || '').toUpperCase() === 'PAID' ||
  normalizeStatus(request?.status) === 'PAID';

const isPaymentExpiredFlag = (request) =>
  request?.paymentExpired === true ||
  request?.payment_expired === true ||
  Number(request?.paymentExpired) === 1 ||
  Number(request?.payment_expired) === 1;

const getApprovedAt = (request) =>
  toDate(
    request?.approvedAt || request?.approved_at ||
    request?.statusUpdatedAt || request?.status_updated_at ||
    request?.updatedAt || request?.updated_at ||
    request?.createdAt || request?.created_at
  );

const getLessonStart = (request) =>
  toDateTime(
    request?.date || request?.lesson_date,
    request?.startTime || request?.start_time
  );

const getPaymentDeadline = (request, lessonStart) => {
  const explicitDeadline = toDate(
    request?.paymentDeadlineAt || request?.payment_deadline_at || request?.payment_deadline
  );
  const approvedAt = getApprovedAt(request);

  const validLessonStart =
    lessonStart instanceof Date && !Number.isNaN(lessonStart.getTime()) ? lessonStart : null;
  const validApprovedAt =
    approvedAt instanceof Date && !Number.isNaN(approvedAt.getTime()) ? approvedAt : null;
  const validExplicitDeadline =
    explicitDeadline instanceof Date && !Number.isNaN(explicitDeadline.getTime())
      ? explicitDeadline : null;

  const oneHourBeforeLesson = validLessonStart
    ? new Date(validLessonStart.getTime() - 60 * 60 * 1000) : null;
  const fiveHoursAfterApproval = validApprovedAt
    ? new Date(validApprovedAt.getTime() + 5 * 60 * 60 * 1000) : null;

  const candidates = [validExplicitDeadline, fiveHoursAfterApproval, oneHourBeforeLesson]
    .filter((d) => d instanceof Date && !Number.isNaN(d.getTime()));

  if (!candidates.length) return null;
  return new Date(Math.min(...candidates.map((d) => d.getTime())));
};

const deriveRequestUiState = (request, nowMs) => {
  const rawStatus = normalizeStatus(request?.status);
  const lessonStart = getLessonStart(request);
  const now = new Date(nowMs);

  if (isPaidRequest(request)) {
    return { uiStatus: 'PAID', deadline: null, timeLeftMs: 0, showCountdown: false, helperText: '' };
  }

  if (rawStatus === 'NOT_APPROVED') {
    return { uiStatus: 'NOT_APPROVED', deadline: null, timeLeftMs: 0, showCountdown: false, helperText: '' };
  }

  if (rawStatus === 'NO_RESPONSE') {
    if (lessonStart instanceof Date && !Number.isNaN(lessonStart.getTime())) {
      const pendingDeadline = new Date(lessonStart.getTime() - 60 * 60 * 1000);
      const timeLeftMs = pendingDeadline.getTime() - now.getTime();
      if (timeLeftMs <= 999) {
        return {
          uiStatus: 'REQUEST_EXPIRED', deadline: pendingDeadline, timeLeftMs: 0,
          showCountdown: false, helperText: 'הבקשה לא טופלה עד שעה לפני תחילת השיעור',
        };
      }
    }
    return { uiStatus: 'NO_RESPONSE', deadline: null, timeLeftMs: 0, showCountdown: false, helperText: '' };
  }

  if (rawStatus === 'APPROVED') {
    const deadline = getPaymentDeadline(request, lessonStart);

    if (isPaymentExpiredFlag(request)) {
      return {
        uiStatus: 'PAYMENT_EXPIRED', deadline, timeLeftMs: 0, showCountdown: false,
        helperText: 'עבר הזמן המותר לתשלום עבור שיעור זה',
      };
    }

    if (lessonStart instanceof Date && !Number.isNaN(lessonStart.getTime())) {
      const hardDeadline = new Date(lessonStart.getTime() - 60 * 60 * 1000);
      if (now.getTime() >= hardDeadline.getTime()) {
        return {
          uiStatus: 'PAYMENT_EXPIRED', deadline: hardDeadline, timeLeftMs: 0,
          showCountdown: false, helperText: 'עבר הזמן המותר לתשלום עבור שיעור זה',
        };
      }
    }

    if (!(deadline instanceof Date) || Number.isNaN(deadline.getTime())) {
      return { uiStatus: 'APPROVED', deadline: null, timeLeftMs: 0, showCountdown: false, helperText: '' };
    }

    const timeLeftMs = deadline.getTime() - now.getTime();
    if (timeLeftMs <= 999) {
      return {
        uiStatus: 'PAYMENT_EXPIRED', deadline, timeLeftMs: 0, showCountdown: false,
        helperText: 'עבר הזמן המותר לתשלום עבור שיעור זה',
      };
    }

    return {
      uiStatus: 'APPROVED', deadline, timeLeftMs, showCountdown: true,
      helperText: `יש לשלם עד ${formatDeadlineTime(deadline)}`,
    };
  }

  return { uiStatus: rawStatus || 'NO_RESPONSE', deadline: null, timeLeftMs: 0, showCountdown: false, helperText: '' };
};

/* ─── Sub-components ───────────────────────────────────────────────────────── */

const StatusBadge = ({ status }) => {
  const { className, label } = STATUS_MAP[status] ?? {
    className: 'bg-gray-100 text-gray-700 border-gray-200',
    label: status || '—',
  };
  return (
    <span className={`flex-shrink-0 px-2.5 py-0.5 text-[10px] sm:text-[11px] rounded-full border font-medium ${className}`}>
      {label}
    </span>
  );
};

/* ─── Component ────────────────────────────────────────────────────────────── */

const RequestItem = ({ request, onRemove, serverNowMs }) => {
  const initialNow = Number.isFinite(serverNowMs) ? serverNowMs : Date.now();
  const [tickNow, setTickNow] = useState(initialNow);
  const serverOffsetRef = useRef(0);

  useEffect(() => {
    if (Number.isFinite(serverNowMs)) {
      serverOffsetRef.current = serverNowMs - Date.now();
      setTickNow(Date.now() + serverOffsetRef.current);
    }
  }, [serverNowMs]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setTickNow(Date.now() + serverOffsetRef.current);
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  const derived = useMemo(
    () => deriveRequestUiState(request, tickNow),
    [request, tickNow]
  );

  const statusMeta = STATUS_MAP[derived.uiStatus] ?? STATUS_MAP['NO_RESPONSE'];

  const { day, month } = useMemo(
    () => formatHeDayMonth(request?.date),
    [request?.date]
  );

  const displayDate = useMemo(() => formatHeDate(request?.date), [request?.date]);

  const timeRange = useMemo(() => {
    const s = String(request?.startTime || request?.start_time || '').slice(0, 5);
    const e = String(request?.endTime || request?.end_time || '').slice(0, 5);
    return s && e ? `${s} – ${e}` : '';
  }, [request?.startTime, request?.start_time, request?.endTime, request?.end_time]);

  const displayPrice = useMemo(() => {
    const n = Number(request?.price);
    return Number.isFinite(n) ? n.toFixed(0) : String(request?.price ?? '');
  }, [request?.price]);

  const countdownText = useMemo(() => {
    if (!derived.showCountdown) return '';
    return formatCountdown(derived.timeLeftMs);
  }, [derived.showCountdown, derived.timeLeftMs]);

  const isExpiredState = derived.uiStatus === 'PAYMENT_EXPIRED' || derived.uiStatus === 'REQUEST_EXPIRED';
  const isPaidState = derived.uiStatus === 'PAID';

  return (
    <div className="group relative bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300 transition-all duration-200">

      {/* Left accent bar */}
      <div
        className="absolute top-0 right-0 w-1 h-full rounded-r-xl"
        style={{ backgroundColor: statusMeta.accentColor }}
      />

      <div className="flex min-w-0">

        {/* Date thumbnail */}
        <div className={`w-20 sm:w-24 flex-shrink-0 border-l border-gray-100 flex flex-col items-center justify-center gap-1 self-stretch py-3 ${statusMeta.thumbBg}`}>
          <span className="text-2xl sm:text-3xl font-semibold text-gray-800 leading-none tabular-nums">
            {day}
          </span>
          <span className="text-[10px] sm:text-xs text-gray-400 tracking-wide">
            {month}
          </span>
          <div
            className="mt-1 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${statusMeta.thumbIconColor}18` }}
          >
            <CalendarIcon
              className="w-3.5 h-3.5"
              style={{ color: statusMeta.thumbIconColor }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pr-3 pl-3 py-3 flex flex-col justify-between gap-1.5 min-h-[90px] sm:min-h-[100px]">

          {/* Row 1: title + badges */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
            <h3 className="text-xs sm:text-sm font-semibold text-gray-900 truncate leading-snug min-w-0 flex-shrink">
              שיעור ב-{displayDate}
            </h3>
            <StatusBadge status={derived.uiStatus} />
            {derived.showCountdown && (
              <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] sm:text-[11px] rounded-full border font-medium bg-green-50 text-green-800 border-green-200">
                <TimerIcon className="w-3 h-3" />
                <span dir="ltr" className="tabular-nums">{countdownText}</span>
              </span>
            )}
          </div>

          {/* Row 2: date + time meta */}
          <div className="flex items-center gap-3 text-[11px] sm:text-xs text-gray-400 flex-wrap">
            <span className="flex items-center gap-1 flex-shrink-0">
              <CalendarIcon className="w-3 h-3" />
              <span>{displayDate}</span>
            </span>
            {timeRange && (
              <span className="flex items-center gap-1 flex-shrink-0">
                <ClockIcon className="w-3 h-3" />
                <span dir="ltr" className="tabular-nums">{timeRange}</span>
              </span>
            )}
          </div>

          {/* Row 3: helper text + price */}
          <div className="flex items-center justify-between gap-2 min-w-0 pt-1.5 border-t border-gray-100">
            {derived.helperText ? (
              <span
                className="text-[10px] sm:text-[11px] leading-snug truncate"
                style={{ color: isExpiredState ? '#A32D2D' : '#9ca3af' }}
              >
                {derived.helperText}
              </span>
            ) : (
              <span />
            )}
            <span
              className="font-bold whitespace-nowrap tabular-nums text-sm flex-shrink-0"
              style={{ color: isPaidState ? '#534AB7' : isExpiredState ? '#A32D2D' : '#185FA5' }}
            >
              ₪{displayPrice}
            </span>
          </div>

        </div>
      </div>

      {/* Trash button — shown on hover, fully rounded */}
      <button
        type="button"
        onClick={() => onRemove?.(request?.id)}
        aria-label="הסר בקשה"
        title="הסר בקשה"
        className="absolute top-2 left-2 z-20 w-7 h-7 flex items-center justify-center rounded-full bg-red-50 text-red-300 border border-red-100 hover:bg-red-100 hover:text-red-500 hover:border-red-300 transition-all duration-150"
      >
        <TrashIcon size={13} />
      </button>

    </div>
  );
};

export default RequestItem;